import { useMemo, useState } from 'react';
import { BellRing, CheckCheck, ExternalLink, Mail } from 'lucide-react';
import { useUrlFilters } from '../../../hooks/admin/useUrlFilters';
import { useAckAlert, useAdminAlerts, useSendTestAlertEmail } from '../../../hooks/admin/useAdminAlerts';
import { digestPreviewUrl } from '../../../lib/adminApi';
import { booleanField, pageField } from '../../../lib/adminFilters';
import { getApiErrorMessage } from '../../../lib/apiError';
import { formatDateTime } from '../../../lib/utils';
import Button from '../../../components/ui/Button';
import ExportJsonButton from '../../../components/admin/ExportJsonButton';
import { collectAlertsExport, securityExportFilename } from '../../../lib/adminSecurityExport';
import Pagination from '../../../components/admin/Pagination';
import InlineNotice from '../../../components/admin/InlineNotice';
import { Pill, SeverityBadge } from '../../../components/admin/StatusBadge';
import type { AdminAlert } from '../../../types/admin';

const PAGE_SIZE = 25;

const alertsSchema = {
  showAcked: booleanField(false),
  page: pageField(),
};

export default function AlertsTab() {
  const { filters, setFilters } = useUrlFilters(alertsSchema);
  const query = useAdminAlerts({
    acknowledged: filters.showAcked ? undefined : '0',
    page: filters.page,
    limit: PAGE_SIZE,
  });
  const ackMutation = useAckAlert();
  const testEmail = useSendTestAlertEmail();
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const items = useMemo(() => {
    const rows = query.data?.items ?? [];
    // Unacknowledged first, then newest first.
    return [...rows].sort((a, b) => {
      const ackA = a.acknowledgedAt ? 1 : 0;
      const ackB = b.acknowledgedAt ? 1 : 0;
      if (ackA !== ackB) return ackA - ackB;
      return new Date(b.firedAt).getTime() - new Date(a.firedAt).getTime();
    });
  }, [query.data]);

  const ack = async (alert: AdminAlert) => {
    setNotice(null);
    try {
      await ackMutation.mutateAsync({ id: alert.id });
    } catch (err) {
      setNotice({ tone: 'error', text: getApiErrorMessage(err, 'Failed to acknowledge the alert') });
    }
  };

  const sendTest = async () => {
    setNotice(null);
    try {
      const res = await testEmail.mutateAsync();
      setNotice({ tone: 'success', text: `Test email sent to ${res.sentTo}. Check the inbox (and spam folder).` });
    } catch (err) {
      setNotice({ tone: 'error', text: getApiErrorMessage(err, 'Failed to send the test email') });
    }
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const unacked = query.data?.unacknowledgedCount ?? 0;

  return (
    <div className="space-y-4">
      <div className="glass-card flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-2 text-glass-text">
            <BellRing className={`h-4 w-4 ${unacked > 0 ? 'text-[rgb(var(--warn))]' : 'text-glass-text-3'}`} aria-hidden />
            {unacked} unacknowledged
          </span>
          <label className="inline-flex items-center gap-2 text-glass-text-2">
            <input
              type="checkbox"
              checked={filters.showAcked}
              onChange={(e) => setFilters({ showAcked: e.target.checked })}
              className="rounded border-glass accent-accent2"
            />
            Show acknowledged
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={digestPreviewUrl()}
            target="_blank"
            rel="noreferrer"
            className="btn-glass inline-flex items-center gap-1.5 px-3 py-1.5 text-sm"
          >
            <ExternalLink className="h-4 w-4" aria-hidden />
            Preview digest
          </a>
          <ExportJsonButton
            label="Download this tab"
            filename={() => securityExportFilename('alerts')}
            collect={async () => {
              const result = await collectAlertsExport(filters.showAcked ? undefined : '0');
              return {
                exportedAt: new Date().toISOString(),
                showAcknowledged: filters.showAcked,
                truncated: result.truncated,
                total: result.total,
                items: result.items,
              };
            }}
          />
          <Button variant="outline" size="sm" onClick={() => void sendTest()} disabled={testEmail.isPending}>
            <Mail className="mr-2 h-4 w-4" aria-hidden />
            {testEmail.isPending ? 'Sending…' : 'Send test email'}
          </Button>
        </div>
      </div>

      {notice && (
        <InlineNotice tone={notice.tone} onDismiss={() => setNotice(null)}>
          {notice.text}
        </InlineNotice>
      )}
      {query.isError && <InlineNotice tone="error">{getApiErrorMessage(query.error, 'Failed to load alerts')}</InlineNotice>}

      <div className="glass-card overflow-hidden">
        {query.isPending ? (
          <div className="space-y-3 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-glass-3/60" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-glass-text-2">
            <CheckCheck className="h-6 w-6 text-[rgb(var(--pos))]" aria-hidden />
            {filters.showAcked ? 'No alerts have fired yet.' : 'Nothing needs your attention.'}
          </div>
        ) : (
          <ul className="divide-y divide-glass">
            {items.map((alert) => {
              const open = expanded.has(alert.id);
              const acked = Boolean(alert.acknowledgedAt);
              return (
                <li key={alert.id} className={`p-4 ${acked ? 'opacity-70' : ''}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <SeverityBadge severity={alert.severity} />
                        <span className="font-mono text-[11px] text-glass-text-3">{alert.ruleKey}</span>
                        {alert.subject && <span className="font-mono text-[11px] text-glass-text-3">· {alert.subject}</span>}
                        {alert.emailedAt ? (
                          <Pill tone="info" title={`Emailed ${formatDateTime(alert.emailedAt)}`}>
                            emailed
                          </Pill>
                        ) : (
                          <Pill tone="muted" title="No email was sent (disabled or failed)">
                            not emailed
                          </Pill>
                        )}
                        {acked && (
                          <Pill tone="pos" title={formatDateTime(alert.acknowledgedAt as string)}>
                            acknowledged
                          </Pill>
                        )}
                      </div>
                      <button type="button" onClick={() => toggle(alert.id)} className="mt-1 block text-left text-sm font-semibold text-glass-text hover:text-accent2">
                        {alert.title}
                      </button>
                      <div className="text-xs text-glass-text-3">Fired {formatDateTime(alert.firedAt)}</div>
                      {open && (
                        <div className="mt-3 space-y-2">
                          <p className="whitespace-pre-wrap text-sm text-glass-text-2">{alert.body}</p>
                          {alert.details && (
                            <pre className="max-h-64 overflow-auto rounded-lg border border-glass bg-glass-1 p-3 font-mono text-xs text-glass-text glass-scroll">
                              {JSON.stringify(alert.details, null, 2)}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                    {!acked && (
                      <Button variant="outline" size="sm" onClick={() => void ack(alert)} disabled={ackMutation.isPending}>
                        <CheckCheck className="mr-2 h-4 w-4" aria-hidden />
                        Acknowledge
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <Pagination pagination={query.data?.pagination} onPageChange={(page) => setFilters({ page })} label="alerts" isFetching={query.isFetching && !query.isPending} />
      </div>
    </div>
  );
}
